import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Palette, Loader2, Wand2, Plus, X, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;
const EMPTY = { colors: [], font_style: "", visual_style: "", tone: "", rules: "", active: true };

const BrandForm = ({ b, setB }) => {
  const setCol = (i, k, v) => setB({ ...b, colors: b.colors.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <p className="text-xs font-medium text-zinc-600">Culori</p>
        {b.colors.map((c, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`brand-color-${i}`}>
            <input type="color" value={c.hex} onChange={(e) => setCol(i, "hex", e.target.value)} className="h-8 w-10 cursor-pointer rounded border" data-testid={`brand-color-picker-${i}`} />
            <Input value={c.hex} onChange={(e) => setCol(i, "hex", e.target.value)} className="w-28 font-mono text-xs" data-testid={`brand-color-hex-${i}`} />
            <Input value={c.name} onChange={(e) => setCol(i, "name", e.target.value)} placeholder="Nume (ex. Mov principal)" className="text-xs" data-testid={`brand-color-name-${i}`} />
            <button onClick={() => setB({ ...b, colors: b.colors.filter((_, j) => j !== i) })} className="text-zinc-400 hover:text-rose-500" data-testid={`brand-color-remove-${i}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
        {b.colors.length < 6 && <button data-testid="brand-color-add" onClick={() => setB({ ...b, colors: [...b.colors, { hex: "#7c3aed", name: "" }] })} className="flex items-center gap-1 text-xs text-zinc-600 hover:text-amber-600"><Plus className="h-3.5 w-3.5" /> Adaugă culoare</button>}
      </div>
      <label className="block text-xs font-medium text-zinc-600">Stil font (pentru AI, în engleză)
        <Input data-testid="brand-font-input" value={b.font_style} onChange={(e) => setB({ ...b, font_style: e.target.value })} placeholder="ex. bold geometric sans-serif, like Montserrat" className="mt-1" />
      </label>
      <label className="block text-xs font-medium text-zinc-600">Stil vizual (pentru AI, în engleză)
        <Textarea data-testid="brand-visual-input" value={b.visual_style} onChange={(e) => setB({ ...b, visual_style: e.target.value })} rows={2} className="mt-1" />
      </label>
      <label className="block text-xs font-medium text-zinc-600">Tonul textelor (română)
        <Textarea data-testid="brand-tone-input" value={b.tone} onChange={(e) => setB({ ...b, tone: e.target.value })} rows={2} className="mt-1" />
      </label>
      <label className="block text-xs font-medium text-zinc-600">Reguli din brand book (pentru AI, în engleză)
        <Textarea data-testid="brand-rules-input" value={b.rules || ""} onChange={(e) => setB({ ...b, rules: e.target.value })} rows={4} placeholder="- Logo only on white or brand blue backgrounds" className="mt-1 text-xs" />
      </label>
      <label className="flex items-center gap-2 text-sm text-zinc-700">
        <Switch data-testid="brand-active-switch" checked={b.active} onCheckedChange={(v) => setB({ ...b, active: v })} /> Aplică automat la toate creativele noi
      </label>
    </div>
  );
};

export const BrandKit = () => {
  const [saved, setSaved] = useState(null);
  const [b, setB] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const book = b.book || saved?.book;
  useEffect(() => { axios.get(`${API}/brand`).then((r) => setSaved(r.data?.updated_at ? r.data : null)).catch(() => {}); }, []);

  const show = () => { setB(saved ? { ...EMPTY, ...saved } : EMPTY); setOpen(true); };
  const suggest = async () => {
    setBusy("ai");
    try { const r = await axios.post(`${API}/brand/suggest`, {}, { timeout: 90000 }); setB({ ...EMPTY, ...r.data }); toast.success("Propunere AI gata — verific-o și salveaz-o"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };
  const uploadBook = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("book");
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await axios.post(`${API}/brand/book`, fd, { timeout: 240000 });
      setB({ ...EMPTY, ...r.data });
      toast.success("Am citit brand book-ul — verifică propunerea și apasă „Confirm și salvez”");
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setBusy("");
  };
  const save = async () => {
    setBusy("save");
    try { const r = await axios.put(`${API}/brand`, b); setSaved(r.data); setOpen(false); toast.success("Kit de brand salvat"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };

  return (
    <>
      <button data-testid="brand-kit-btn" onClick={show} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-2.5 text-left hover:border-amber-400/60">
        <Palette className="h-5 w-5 text-amber-400" />
        <div className="text-xs">
          <p className="font-medium text-zinc-200">Kit de brand</p>
          <div className="mt-0.5 flex items-center gap-1" data-testid="brand-kit-status">
            {saved?.colors?.length ? saved.colors.map((c, i) => <span key={i} className="h-3 w-3 rounded-full border border-zinc-700" style={{ background: c.hex }} />) : <span className="text-zinc-500">nesetat</span>}
            {saved && <span className={`ml-1 ${saved.active ? "text-emerald-400" : "text-zinc-500"}`}>{saved.active ? "activ" : "oprit"}</span>}
          </div>
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="brand-kit-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Kit de brand BRAGUS</DialogTitle></DialogHeader>
          <button data-testid="brand-suggest-btn" onClick={suggest} disabled={!!busy} className="flex items-center justify-center gap-2 rounded-full border border-amber-500 py-2 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50">
            {busy === "ai" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Propune cu AI (din logo și bragus.ro)
          </button>
          <button data-testid="brand-book-upload-btn" onClick={() => fileRef.current?.click()} disabled={!!busy} className="flex items-center justify-center gap-2 rounded-full border border-amber-500 py-2 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50">
            {busy === "book" ? <><Loader2 className="h-4 w-4 animate-spin" /> AI-ul citește brand book-ul (~1 min)…</> : <><FileText className="h-4 w-4" /> Încarcă brand book (PDF)</>}
          </button>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" hidden onChange={uploadBook} data-testid="brand-book-input" />
          {book && <p className="-mt-1 text-center text-xs text-zinc-500" data-testid="brand-book-info">Brand book: {book.filename} · {book.pages} pagini</p>}
          <BrandForm b={b} setB={setB} />
          <button data-testid="brand-save-btn" onClick={save} disabled={!!busy} className="flex items-center justify-center gap-2 rounded-full bg-amber-400 py-2.5 text-sm font-semibold text-zinc-950 disabled:opacity-50">
            {busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Confirm și salvez
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
